import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { authService } from '../../services/AuthService';
import { PasswordResetStep } from '../../types/auth';
import { Mail, Key, Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';

interface ForgotPasswordModalProps {
  /** Si el modal está abierto */
  isOpen: boolean;
  /** Función para cerrar el modal */
  onClose: () => void;
}

/**
 * Modal para el reseteo de contraseña con tres pasos:
 * 1. Introducir email
 * 2. Verificar código de 4 caracteres
 * 3. Establecer nueva contraseña
 */
export const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  isOpen,
  onClose
}) => {
  const [currentStep, setCurrentStep] = useState<PasswordResetStep>(PasswordResetStep.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * Limpia el formulario y estado cuando se abre/cierra el modal
   */
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  /**
   * Resetea el formulario a su estado inicial
   */
  const resetForm = () => {
    setCurrentStep(PasswordResetStep.EMAIL);
    setEmail('');
    setCode('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    setErrors({});
    setIsLoading(false);
    setShowPasswords({ new: false, confirm: false });
    setSuccessMessage('');
    setErrorMessage('');
  };

  /**
   * Limpia los mensajes de error y éxito
   */
  const clearMessages = () => {
    setErrorMessage('');
    setSuccessMessage('');
    setErrors({});
  };

  /**
   * Alterna la visibilidad de una contraseña
   */
  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  /**
   * Valida el email
   */
  const validateEmail = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!email) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Ingresa un email válido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Valida el código de verificación
   */
  const validateCode = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!code) {
      newErrors.code = 'El código es requerido';
    } else if (code.length !== 4) {
      newErrors.code = 'El código debe tener 4 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Valida las contraseñas
   */
  const validatePasswords = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!newPassword) {
      newErrors.newPassword = 'La nueva contraseña es requerida';
    } else if (newPassword.length < 6) {
      newErrors.newPassword = 'La contraseña debe tener al menos 6 caracteres';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirmar la contraseña es requerido';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Maneja el envío del email
   */
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    if (!validateEmail()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.forgotPassword({ email });
      setSuccessMessage(response.message);
      setCurrentStep(PasswordResetStep.CODE);
    } catch (error: any) {
      setErrorMessage(error.message || 'Error al enviar el código');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Maneja la verificación del código
   */
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    if (!validateCode()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.verifyResetCode({ email, code });
      setSuccessMessage(response.message);
      // Guardar el resetToken para el siguiente paso
      if (response.resetToken) {
        setResetToken(response.resetToken);
      }
      setCurrentStep(PasswordResetStep.PASSWORD);
    } catch (error: any) {
      setErrorMessage(error.message || 'Código inválido');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Maneja el reseteo de la contraseña
   */
  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.resetPassword({ 
        resetToken,
        newPassword,
        confirmPassword
      });
      setSuccessMessage(response.message);
      
      // Cerrar el modal después de 2 segundos
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error: any) {
      setErrorMessage(error.message || 'Error al cambiar la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Vuelve al paso anterior
   */
  const goBack = () => {
    clearMessages();
    if (currentStep === PasswordResetStep.CODE) {
      setCode(''); // Limpiar el código cuando se vuelve atrás
      setCurrentStep(PasswordResetStep.EMAIL);
    } else if (currentStep === PasswordResetStep.PASSWORD) {
      setCurrentStep(PasswordResetStep.CODE);
    }
  };

  /**
   * Obtiene el título del paso actual
   */
  const getStepTitle = () => {
    switch (currentStep) {
      case PasswordResetStep.EMAIL:
        return 'Recuperar Contraseña';
      case PasswordResetStep.CODE:
        return 'Verificar Código';
      case PasswordResetStep.PASSWORD:
        return 'Nueva Contraseña';
      default:
        return 'Recuperar Contraseña';
    }
  };

  /**
   * Obtiene el icono del paso actual
   */
  const getStepIcon = () => {
    switch (currentStep) {
      case PasswordResetStep.EMAIL:
        return <Mail className="w-5 h-5 text-emerald-400" />;
      case PasswordResetStep.CODE:
        return <Key className="w-5 h-5 text-emerald-400" />;
      case PasswordResetStep.PASSWORD:
        return <Lock className="w-5 h-5 text-emerald-400" />;
      default:
        return <Mail className="w-5 h-5 text-emerald-400" />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-secondary-700">
        <div className="flex items-center gap-2">
          {currentStep !== PasswordResetStep.EMAIL && (
            <button
              onClick={goBack}
              className="p-1 text-secondary-400 hover:text-secondary-300 transition-colors mr-2"
              disabled={isLoading}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            {getStepIcon()}
            {getStepTitle()}
          </h2>
        </div>
      </div>

      {/* Indicador de pasos */}
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-4">
          {[PasswordResetStep.EMAIL, PasswordResetStep.CODE, PasswordResetStep.PASSWORD].map((step, index) => (
            <div key={step} className="flex items-center">
              <div className={`
                w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                ${currentStep === step 
                  ? 'bg-emerald-500 text-white' 
                  : Object.values(PasswordResetStep).indexOf(currentStep) > Object.values(PasswordResetStep).indexOf(step)
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-secondary-700 text-secondary-400'
                }
              `}>
                {index + 1}
              </div>
              {index < 2 && (
                <div className={`
                  w-12 h-0.5 mx-2
                  ${Object.values(PasswordResetStep).indexOf(currentStep) > index
                    ? 'bg-emerald-500/40'
                    : 'bg-secondary-700'
                  }
                `} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mensaje de éxito */}
      {successMessage && (
        <div className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/30 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <span className="text-emerald-100 text-sm">
            {successMessage}
          </span>
        </div>
      )}

      {/* Mensaje de error */}
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-100 text-sm">
            {errorMessage}
          </span>
        </div>
      )}

      {/* Paso 1: Email */}
      {currentStep === PasswordResetStep.EMAIL && (
        <form onSubmit={handleEmailSubmit} className="space-y-6">
          <div className="text-center mb-6">
            <p className="text-secondary-300 text-sm">
              Ingresa tu email y te enviaremos un código de verificación para restablecer tu contraseña.
            </p>
          </div>

          <Input
            label="Email *"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errors.email) {
                setErrors(prev => ({ ...prev, email: '' }));
              }
            }}
            placeholder="tu@email.com"
            error={errors.email}
            icon={<Mail size={20} />}
            disabled={isLoading}
            required
          />

          <div className="flex gap-3 pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Enviando...' : 'Enviar Código'}
            </Button>
          </div>
        </form>
      )}

      {/* Paso 2: Código */}
      {currentStep === PasswordResetStep.CODE && (
        <form onSubmit={handleCodeSubmit} className="space-y-6">
          <div className="text-center mb-6">
            <p className="text-secondary-300 text-sm">
              Hemos enviado un código de 4 caracteres a <span className="text-emerald-400 font-medium">{email}</span>.
              Ingrésalo a continuación.
            </p>
          </div>

          <Input
            label="Código de Verificación *"
            type="text"
            value={code}
            onChange={(e) => {
              const value = e.target.value.toUpperCase().slice(0, 4);
              setCode(value);
              if (errors.code) {
                setErrors(prev => ({ ...prev, code: '' }));
              }
            }}
            placeholder="XXXX"
            error={errors.code}
            icon={<Key size={20} />}
            disabled={isLoading}
            maxLength={4}
            className="text-center text-lg tracking-widest"
            required
          />

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <p className="text-blue-300 text-sm text-center">
              ¿No recibiste el código? Revisa tu carpeta de spam o vuelve al paso anterior para reenviar.
            </p>
          </div>

          <div className="flex gap-3 pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={goBack}
              disabled={isLoading}
              className="flex-1"
            >
              Volver
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading || code.length !== 4}
              className="flex-1"
            >
              {isLoading ? 'Verificando...' : 'Verificar'}
            </Button>
          </div>
        </form>
      )}

      {/* Paso 3: Nueva Contraseña */}
      {currentStep === PasswordResetStep.PASSWORD && (
        <form onSubmit={handlePasswordSubmit} className="space-y-6">
          <div className="text-center mb-6">
            <p className="text-secondary-300 text-sm">
              Ahora puedes establecer tu nueva contraseña.
            </p>
          </div>

          {/* Nueva contraseña */}
          <div className="relative">
            <Input
              label="Nueva Contraseña *"
              type={showPasswords.new ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (errors.newPassword) {
                  setErrors(prev => ({ ...prev, newPassword: '' }));
                }
              }}
              placeholder="Ingresa tu nueva contraseña"
              error={errors.newPassword}
              icon={<Lock size={20} />}
              disabled={isLoading}
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute right-3 top-9 text-secondary-400 hover:text-secondary-300 transition-colors"
              disabled={isLoading}
            >
              {showPasswords.new ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Confirmar nueva contraseña */}
          <div className="relative">
            <Input
              label="Confirmar Nueva Contraseña *"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (errors.confirmPassword) {
                  setErrors(prev => ({ ...prev, confirmPassword: '' }));
                }
              }}
              placeholder="Confirma tu nueva contraseña"
              error={errors.confirmPassword}
              icon={<Lock size={20} />}
              disabled={isLoading}
              required
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute right-3 top-9 text-secondary-400 hover:text-secondary-300 transition-colors"
              disabled={isLoading}
            >
              {showPasswords.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {/* Información de seguridad */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <h4 className="text-blue-400 font-medium mb-2">Requisitos de la contraseña:</h4>
            <ul className="text-blue-300 text-sm space-y-1">
              <li>• Mínimo 6 caracteres</li>
              <li>• Se recomienda usar una combinación de letras, números y símbolos</li>
            </ul>
          </div>

          <div className="flex gap-3 pt-6">
            <Button
              type="button"
              variant="secondary"
              onClick={goBack}
              disabled={isLoading}
              className="flex-1"
            >
              Volver
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isLoading}
              className="flex-1"
            >
              {isLoading ? 'Cambiando...' : 'Cambiar Contraseña'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
};